import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Alert, Button, ProgressBar } from "@heroui/react";
import { cn } from "@/lib/utils";
import { REPLAY_RECORDED, readReplayLog, type ReplayLogEntry } from "../chart/replay/replay";
import {
  CHALLENGE_DAYS, DAILY_GOAL, GOAL_TRADES, RULES_GOAL, SCRATCH_R, challengeStart, challengeStats, challengeTrades, useChallengeStart,
  type ChallengeStats, type ChallengeTrade, type DayStat,
} from "./challenge";
import { StatusIcon } from "./parts";
import type { TrackedTrade } from "./deskData";

const DAY_MS = 86_400_000;
const MONTHS = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
const dayLabel = (ms: number) => { const d = new Date(ms); return `${d.getUTCDate()} ${MONTHS[d.getUTCMonth()]}`; };
const signedR = (r: number) => `${r >= 0 ? "+" : "−"}${Math.abs(r).toFixed(2)}R`;
const usd = (v: number) => `${v < 0 ? "−" : "+"}$${Math.abs(v).toFixed(2)}`;

/** Replay trades with their dates, kept fresh as new ones close. */
function useReplayLog(): ReplayLogEntry[] {
  const [log, setLog] = useState(readReplayLog);
  useEffect(() => {
    const reload = () => setLog(readReplayLog());
    window.addEventListener(REPLAY_RECORDED, reload);
    window.addEventListener("storage", reload);
    return () => {
      window.removeEventListener(REPLAY_RECORDED, reload);
      window.removeEventListener("storage", reload);
    };
  }, []);
  return log;
}

/**
 * The 30-day practice plan: skill before money. Counts every trade you log
 * (real or practice) and every Replay trade, by day — each day's wins,
 * losses and net R — against the milestone. Lives on the Trades page.
 */
export function ChallengePanel({ trades, riskUsd }: { trades: TrackedTrade[]; riskUsd: number }) {
  const start = useChallengeStart();
  const replay = useReplayLog();
  const [confirmReset, setConfirmReset] = useState(false);

  if (start === null) return <ChallengeIntro onStart={() => challengeStart.set(Date.now())} />;

  const now = Date.now();
  const s = challengeStats(challengeTrades(trades, replay, start), start, now);
  const over = s.day > CHALLENGE_DAYS;
  const startDay = Math.floor(start / DAY_MS) * DAY_MS;
  const done = s.milestone.trades && s.milestone.rules && s.milestone.avgR;
  const todayIdx = s.day - 1;

  return (
    <div className="space-y-4 p-4">
      <div className="flex flex-wrap items-baseline justify-between gap-3">
        <div>
          <h3 className="text-base font-semibold text-foreground">
            {over ? "Your 30 days are done" : <>30-day plan · day <span className="num">{s.day}</span> of {CHALLENGE_DAYS}</>}
          </h3>
          <p className="text-xs text-muted">
            Started {dayLabel(startDay)} · {over ? "ended" : "ends"} {dayLabel(startDay + (CHALLENGE_DAYS - 1) * DAY_MS)} · days follow the desk's 00:00 UTC reset
          </p>
        </div>
        {confirmReset ? (
          <div className="flex flex-wrap items-center gap-2 text-xs text-muted">
            <span>Start over? Your trades stay in the journal; only the count restarts.</span>
            <Button size="sm" variant="danger-soft" onPress={() => { challengeStart.set(Date.now()); setConfirmReset(false); }}>Yes, start over</Button>
            <Button size="sm" variant="ghost" onPress={() => setConfirmReset(false)}>Cancel</Button>
          </div>
        ) : (
          <Button size="sm" variant="ghost" onPress={() => setConfirmReset(true)}>{over ? "Start another 30 days" : "Start over"}</Button>
        )}
      </div>

      {done && (
        <Alert status="success">
          <Alert.Indicator />
          <Alert.Content>
            <Alert.Title>Milestone reached</Alert.Title>
            <Alert.Description>
              {GOAL_TRADES} trades, the real ones by the rules, and an average above zero. If you add real money, keep the same small risk
              and keep logging: the next 50 trades check this one.
            </Alert.Description>
          </Alert.Content>
        </Alert>
      )}

      <div className="grid gap-5 lg:grid-cols-[minmax(0,1.5fr)_minmax(0,1fr)]">
        <DayByDay days={s.days} record={s.dayRecord} todayIdx={todayIdx} riskUsd={riskUsd} />

        <div className="space-y-5">
          {/* Today. */}
          {!over && (
            <section aria-label="Today">
              <p className="mb-2 text-2xs font-medium uppercase tracking-wide text-muted">Today</p>
              <p className="text-sm text-foreground">
                <span className="num text-lg font-semibold">{Math.min(s.today, 99)}</span> of {DAILY_GOAL} practice trades
              </p>
              <ProgressBar aria-label={`${s.today} of ${DAILY_GOAL} trades today`} value={Math.min(100, (s.today / DAILY_GOAL) * 100)} size="sm" color={s.today >= DAILY_GOAL ? "success" : "accent"} className="mt-1.5">
                <ProgressBar.Track><ProgressBar.Fill /></ProgressBar.Track>
              </ProgressBar>
              <p className="mt-2 text-xs leading-relaxed text-muted">
                {s.today >= DAILY_GOAL
                  ? "Done for today. More trades won't make you better faster than a good review will."
                  : <>No Ready signal? Use Replay under the <Link to="/" className="text-foreground underline underline-offset-2">desk chart</Link> (Random day), or play a round of <Link to="/games" className="text-foreground underline underline-offset-2">Games</Link> on real past charts.</>}
              </p>
            </section>
          )}

          {/* The milestone before more real money. */}
          <section aria-label="Milestone">
            <p className="mb-2 text-2xs font-medium uppercase tracking-wide text-muted">The milestone</p>
            <ul className="space-y-2">
              <Check met={s.milestone.trades} title={`${GOAL_TRADES} trades`} detail={`${s.trades} so far (real, practice and Replay)`}>
                <ProgressBar aria-label={`${s.trades} of ${GOAL_TRADES} trades`} value={Math.min(100, (s.trades / GOAL_TRADES) * 100)} size="sm" color={s.milestone.trades ? "success" : "accent"} className="mt-1 max-w-56">
                  <ProgressBar.Track><ProgressBar.Fill /></ProgressBar.Track>
                </ProgressBar>
              </Check>
              <Check
                met={s.milestone.rules}
                title={`Real trades by the rules (${Math.round(RULES_GOAL * 100)}%+)`}
                detail={s.rulesPct === null
                  ? "No real trades yet: only Ready signals count as real trades"
                  : `${s.real.followed} of ${s.real.trades} on a signal and inside the day's limits (${Math.round(s.rulesPct * 100)}%)`}
              />
              <Check
                met={s.milestone.avgR}
                title="Average result above zero"
                detail={s.avgR === null
                  ? "No closed trades yet"
                  : `${signedR(s.avgR)} a trade over ${s.closed} closed${riskUsd > 0 ? ` (≈ ${usd(s.avgR * riskUsd)} a trade at your $${riskUsd.toFixed(0)} risk)` : ""}`}
              />
            </ul>
            <p className="mt-3 text-xs text-muted">
              Last 7 days: <span className="num text-foreground">{s.last7.trades}</span> closed
              {s.last7.avgR !== null && <>, average <span className="num text-foreground">{signedR(s.last7.avgR)}</span></>}
              {s.winRate !== null && <> · won <span className="num text-foreground">{Math.round(s.winRate * 100)}%</span> overall</>}
            </p>
          </section>
        </div>
      </div>
    </div>
  );
}

const WEEKDAYS = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];
const KIND_LABEL: Record<ChallengeTrade["kind"], string> = { real: "Real", practice: "Practice", replay: "Replay" };
const shortR = (r: number) => `${r > 0.005 ? "+" : r < -0.005 ? "−" : ""}${Math.abs(r).toFixed(1)}R`;
const timeLabel = (ms: number) => {
  const d = new Date(ms);
  return `${String(d.getUTCHours()).padStart(2, "0")}:${String(d.getUTCMinutes()).padStart(2, "0")}`;
};

/** How a day went, for its colour: up, down, even, still open, nothing, or not here yet. */
function dayTone(d: DayStat, future: boolean): "up" | "down" | "even" | "open" | "none" | "future" {
  if (future) return "future";
  if (d.closed > 0) return d.netR > SCRATCH_R ? "up" : d.netR < -SCRATCH_R ? "down" : "even";
  return d.open > 0 ? "open" : "none";
}

/**
 * The 30 days as a calendar: each day green (it made R), red (it lost R) or
 * grey, with its net R; click one for its trades.
 */
function DayByDay({ days, record, todayIdx, riskUsd }: {
  days: DayStat[]; record: ChallengeStats["dayRecord"]; todayIdx: number; riskUsd: number;
}) {
  const lastWithTrades = days.reduce((last, d, i) => (d.trades.length > 0 && i <= todayIdx ? i : last), -1);
  const [picked, setPicked] = useState<number | null>(null);
  const sel = picked ?? (todayIdx >= 0 && todayIdx < days.length ? todayIdx : Math.max(0, lastWithTrades));
  const day = days[sel];
  return (
    <section aria-label="Day by day">
      <div className="mb-2 flex flex-wrap items-baseline justify-between gap-2">
        <p className="text-2xs font-medium uppercase tracking-wide text-muted">Day by day</p>
        <p className="text-xs text-muted">
          <span className="text-profit">{record.won} up</span> · <span className="text-loss">{record.lost} down</span>
          {record.best !== null && <> · best <span className="num text-foreground">{shortR(record.best)}</span></>}
          {record.worst !== null && <> · worst <span className="num text-foreground">{shortR(record.worst)}</span></>}
        </p>
      </div>
      <div className="grid grid-cols-6 gap-1.5 sm:grid-cols-10">
        {days.map((d, i) => {
          const tone = dayTone(d, i > todayIdx);
          const date = new Date(d.startMs);
          const summary = d.trades.length === 0
            ? "no trades"
            : `${d.trades.length} trade${d.trades.length === 1 ? "" : "s"}${d.closed > 0 ? `, ${d.won} won, ${d.lost} lost, ${shortR(d.netR)}` : ""}${d.open > 0 ? `, ${d.open} open` : ""}`;
          return (
            <button
              key={i}
              type="button"
              disabled={tone === "future"}
              onClick={() => setPicked(i)}
              aria-pressed={i === sel}
              aria-label={`Day ${i + 1}, ${dayLabel(d.startMs)}: ${summary}`}
              title={`Day ${i + 1} · ${WEEKDAYS[date.getUTCDay()]} ${dayLabel(d.startMs)}: ${summary}`}
              className={cn(
                "flex h-12 flex-col items-start justify-between rounded-md border px-1.5 py-1 text-left transition-colors",
                tone === "up" && "border-profit/40 bg-profit-soft",
                tone === "down" && "border-loss/40 bg-loss-soft",
                (tone === "even" || tone === "open") && "border-border bg-default",
                tone === "none" && "border-border bg-surface-secondary",
                tone === "future" && "cursor-default border-dashed border-border/60 bg-transparent opacity-50",
                i === todayIdx && "ring-1 ring-brand",
                i === sel && "outline-2 outline-offset-1 outline-foreground/60",
              )}
            >
              <span className="num text-3xs leading-none text-muted">{date.getUTCDate()}</span>
              <span className={cn(
                "num text-2xs font-semibold leading-none",
                tone === "up" ? "text-profit" : tone === "down" ? "text-loss" : "text-muted",
              )}>
                {d.closed > 0 ? shortR(d.netR) : d.open > 0 ? "open" : tone === "future" ? "" : "·"}
              </span>
            </button>
          );
        })}
      </div>
      {day && <DayDetail day={day} index={sel} riskUsd={riskUsd} />}
    </section>
  );
}

function DayDetail({ day, index, riskUsd }: { day: DayStat; index: number; riskUsd: number }) {
  const date = new Date(day.startMs);
  return (
    <div className="mt-3 rounded-xl border border-border bg-surface-secondary p-3">
      <div className="flex flex-wrap items-baseline justify-between gap-2">
        <p className="text-sm font-medium text-foreground">
          Day {index + 1} · {WEEKDAYS[date.getUTCDay()]} {dayLabel(day.startMs)}
        </p>
        {day.closed > 0 && (
          <p className="text-xs text-muted">
            {day.won} won · {day.lost} lost · <span className={cn("num font-semibold", day.netR > SCRATCH_R ? "text-profit" : day.netR < -SCRATCH_R ? "text-loss" : "text-foreground")}>{signedR(day.netR)}</span>
            {riskUsd > 0 && <> (≈ {usd(day.netR * riskUsd)})</>}
          </p>
        )}
      </div>
      {day.trades.length === 0 ? (
        <p className="mt-1 text-xs text-muted">No trades this day.</p>
      ) : (
        <ul className="mt-2 divide-y divide-separator text-xs">
          {day.trades.map((t, k) => (
            <li key={k} className="flex items-center gap-2 py-1.5">
              <span className="num w-10 shrink-0 text-muted">{timeLabel(t.at)}</span>
              <span className="w-16 shrink-0 text-muted">{KIND_LABEL[t.kind]}</span>
              <span className="min-w-0 flex-1 truncate text-foreground">
                {t.symbol ? `${t.symbol.replace(/USDT$/, "")} ${t.side === "SHORT" ? "sell" : "buy"}` : "Replay trade"}
                {t.kind === "real" && t.followed === false && <span className="ml-1.5 text-warning">off plan</span>}
              </span>
              <span className={cn("num shrink-0 font-semibold", t.r === null ? "text-muted" : t.r > SCRATCH_R ? "text-profit" : t.r < -SCRATCH_R ? "text-loss" : "text-foreground")}>
                {t.r === null ? "open" : signedR(t.r)}
              </span>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

function Check({ met, title, detail, children }: { met: boolean; title: string; detail: string; children?: React.ReactNode }) {
  return (
    <li className="flex gap-2">
      <StatusIcon status={met ? "met" : "pending"} />
      <div className="min-w-0">
        <div className={cn("text-xs font-medium", met ? "text-foreground" : "text-foreground/85")}>{title}</div>
        <div className="num text-2xs text-muted">{detail}</div>
        {children}
      </div>
    </li>
  );
}

function ChallengeIntro({ onStart }: { onStart: () => void }) {
  return (
    <div className="grid gap-4 p-4 lg:grid-cols-[minmax(0,1fr)_auto] lg:items-center">
      <div className="space-y-2">
        <h3 className="text-base font-semibold text-foreground">30 days: skill before money</h3>
        <p className="max-w-2xl text-sm leading-relaxed text-muted">
          For 30 days, take only <span className="text-foreground">Ready</span> signals with real money and practise everything else. Every trade
          you log and every Replay trade counts here, day by day.
        </p>
        <ul className="list-disc space-y-0.5 pl-5 text-xs text-muted">
          <li>Each day: check the desk. No Ready signal? {DAILY_GOAL} practice trades in Replay, about 20–30 minutes.</li>
          <li>Keep the same small risk, and stop for the day at 3 trades or −3R.</li>
          <li>The milestone: {GOAL_TRADES} trades, {Math.round(RULES_GOAL * 100)}% of the real ones by the rules, an average above zero. Only then think about more money.</li>
        </ul>
      </div>
      <Button variant="primary" onPress={onStart}>Start my 30 days</Button>
    </div>
  );
}
